import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  Waves, Check, X, ChevronDown, ChevronUp, Users, Clock, QrCode, Undo2, MessageSquare, UserPlus, Search,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import { getLessonStatus, getLessonStatusLabel, getLessonStatusColor } from "../../lib/lessonUtils";
import LessonQRCode from "../LessonQRCode";
import { getCategoryLabel } from "./ProfessorConstants";
import { API } from "../../App";

/**
 * Check if attendance management is allowed for this lesson.
 * Professors can manage attendance from 1h before start until 2h after end.
 */
const isAttendanceWindowOpen = (lesson) => {
  if (!lesson.date || !lesson.start_time || !lesson.end_time) return false;
  const now = new Date();
  const lisbonNow = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Lisbon" }));
  const [startH, startM] = lesson.start_time.split(":").map(Number);
  const [endH, endM] = lesson.end_time.split(":").map(Number);
  const lessonStart = new Date(lesson.date + "T00:00:00");
  lessonStart.setHours(startH, startM, 0, 0);
  const lessonEnd = new Date(lesson.date + "T00:00:00");
  lessonEnd.setHours(endH, endM, 0, 0);
  // Window: 1h before start → 2h after end
  const windowStart = new Date(lessonStart.getTime() - 60 * 60 * 1000);
  const windowEnd = new Date(lessonEnd.getTime() + 2 * 60 * 60 * 1000);
  // Compare using Lisbon time
  const lisbonMs = lisbonNow.getTime();
  const wsMs = windowStart.getTime();
  const weMs = windowEnd.getTime();
  return lisbonMs >= wsMs && lisbonMs <= weMs;
};

const ProfessorLessonsList = ({
  lessons,
  enrollmentsByLesson,
  onConfirmAttendance,
  onMarkAbsent,
  onCancelLesson,
  onRevertAttendance,
  onExpandLesson,
  onRefreshEnrollments,
  showPast = false,
}) => {
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [addStudentLessonId, setAddStudentLessonId] = useState(null);
  const [addStudentSearch, setAddStudentSearch] = useState("");
  const [addStudentResults, setAddStudentResults] = useState([]);
  const [addStudentLoading, setAddStudentLoading] = useState(false);
  // Inline student creation
  const [showAddStudentCreate, setShowAddStudentCreate] = useState(false);
  const [addNewStudent, setAddNewStudent] = useState({ name: "", email: "", phone: "" });

  // Search students for add-to-lesson
  useEffect(() => {
    if (!addStudentLessonId) return;
    const timer = setTimeout(async () => {
      if (!addStudentSearch.trim()) { setAddStudentResults([]); return; }
      try {
        const res = await axios.get(`${API}/admin/students?search=${encodeURIComponent(addStudentSearch)}&limit=8`, { withCredentials: true });
        const students = res.data.students || res.data || [];
        const enrolledIds = new Set((enrollmentsByLesson[addStudentLessonId] || []).map(e => e.user_id));
        setAddStudentResults(students.filter(s => !enrolledIds.has(s.user_id)));
      } catch { setAddStudentResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addStudentSearch, addStudentLessonId]);

  const handleAddStudent = async (studentId) => {
    setAddStudentLoading(true);
    try {
      const res = await axios.post(`${API}/staff/enrollments`, { lesson_id: addStudentLessonId, user_id: studentId }, { withCredentials: true });
      toast.success(res.data.message);
      if (onRefreshEnrollments) onRefreshEnrollments(addStudentLessonId);
      setAddStudentSearch("");
      setAddStudentResults([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao adicionar aluno");
    } finally { setAddStudentLoading(false); }
  };

  // Create new student and add to lesson
  const handleCreateAndAddStudent = async () => {
    if (!addNewStudent.name.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!addStudentLessonId) return;
    setAddStudentLoading(true);
    try {
      // Create student
      const createRes = await axios.post(`${API}/admin/students`, addNewStudent, { withCredentials: true });
      const newStudentId = createRes.data.user_id;
      // Add to lesson
      await axios.post(`${API}/staff/enrollments`, { lesson_id: addStudentLessonId, user_id: newStudentId }, { withCredentials: true });
      toast.success(`Aluno "${addNewStudent.name}" criado e adicionado à aula`);
      if (onRefreshEnrollments) onRefreshEnrollments(addStudentLessonId);
      setAddNewStudent({ name: "", email: "", phone: "" });
      setShowAddStudentCreate(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar aluno");
    } finally { setAddStudentLoading(false); }
  };

  // Auto-expand the first lesson (only for future view)
  const firstLessonId = !showPast && lessons.length > 0 ? lessons[0].lesson_id : null;
  const activeExpanded = expandedLesson ?? firstLessonId;

  const toggleLesson = (lessonId) => {
    const newExpanded = activeExpanded === lessonId ? "__none__" : lessonId;
    setExpandedLesson(newExpanded);
    if (newExpanded !== "__none__" && onExpandLesson) {
      onExpandLesson(newExpanded);
    }
  };

  if (lessons.length === 0) {
    return (
      <Card className="bg-white border-0 shadow-sm rounded-xl">
        <CardContent className="py-12 text-center">
          <Waves className="h-10 w-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#94A3B8] text-sm">
            {showPast ? "Nenhuma aula passada encontrada" : "Sem aulas futuras agendadas"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="prof-lessons-list">
      {lessons.map((lesson, idx) => {
        const isFirst = !showPast && idx === 0;
        const status = getLessonStatus(lesson);
        const isExpanded = activeExpanded === lesson.lesson_id;
        const lessonEnrollments = enrollmentsByLesson[lesson.lesson_id] || [];
        const allEnrollments = lessonEnrollments.filter(
          (e) => !["cancelled"].includes(e.status)
        );
        const pendingEnrollments = lessonEnrollments.filter(
          (e) => e.status === "enrolled" || e.status === "reserved"
        );
        const canManageAttendance = isAttendanceWindowOpen(lesson);

        return (
          <Card
            key={lesson.lesson_id}
            className={`bg-white border-0 shadow-sm rounded-xl overflow-hidden transition-shadow ${
              isFirst ? "ring-2 ring-[#6697a4]/20 shadow-md" : ""
            }`}
            data-testid={`prof-lesson-card-${lesson.lesson_id}`}
          >
            {/* Lesson Header - always visible, tappable */}
            <button
              type="button"
              className="w-full text-left p-3 sm:p-4 focus:outline-none"
              onClick={() => toggleLesson(lesson.lesson_id)}
              data-testid={`prof-lesson-toggle-${lesson.lesson_id}`}
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div
                  className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${
                    isFirst
                      ? "bg-[#6697a4] text-white"
                      : status === "past"
                      ? "bg-slate-100 text-[#94A3B8]"
                      : "bg-[#6697a4]/10 text-[#6697a4]"
                  }`}
                >
                  <Waves className="h-5 w-5" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0F172A] text-sm sm:text-base">
                      {getCategoryLabel(lesson.lesson_category)}
                    </span>
                    <Badge className={`${getLessonStatusColor(status)} border-0 text-[10px] px-2`}>
                      {getLessonStatusLabel(lesson, isFirst)}
                    </Badge>
                    {lesson.lesson_type === "private" && (
                      <Badge variant="outline" className="text-[10px] border-[#FF7F50] text-[#FF7F50]">
                        Privada
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-[#475569] mt-0.5">
                    {lesson.date && format(parseISO(lesson.date), "EEEE, d MMM", { locale: pt })}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#94A3B8]">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {lesson.start_time} - {lesson.end_time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {lesson.current_enrollments}/{lesson.max_students}
                    </span>
                  </div>
                  {lesson.teachers_data?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1.5">
                      {lesson.teachers_data.map((t) => (
                        <span key={t.user_id} className="text-[10px] text-[#6697a4] bg-[#6697a4]/10 px-1.5 py-0.5 rounded-full">
                          {t.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Expand indicator + pending count */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {pendingEnrollments.length > 0 && !showPast && (
                    <Badge className="bg-[#F59E0B] text-white border-0 text-[10px]">
                      {pendingEnrollments.length} pendente{pendingEnrollments.length > 1 ? "s" : ""}
                    </Badge>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-[#94A3B8]" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-[#94A3B8]" />
                  )}
                </div>
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t border-slate-100">
                {/* Action buttons */}
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-slate-50/50 flex-wrap">
                  <LessonQRCode
                    lessonId={lesson.lesson_id}
                    lessonTitle={getCategoryLabel(lesson.lesson_category)}
                  />
                  {lesson.status !== "cancelled" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[#6697a4] border-[#6697a4]/30 hover:bg-[#6697a4]/5 h-8 text-xs"
                      onClick={(e) => { e.stopPropagation(); setAddStudentLessonId(lesson.lesson_id); setAddStudentSearch(""); setAddStudentResults([]); }}
                      data-testid={`prof-add-student-btn-${lesson.lesson_id}`}
                    >
                      <UserPlus className="h-3 w-3 mr-1" /> Adicionar Aluno
                    </Button>
                  )}
                  {!showPast && lesson.status !== "cancelled" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancelLesson(lesson.lesson_id);
                      }}
                      data-testid={`prof-cancel-lesson-${lesson.lesson_id}`}
                    >
                      <X className="h-3 w-3 mr-1" /> Cancelar Aula
                    </Button>
                  )}
                  {!canManageAttendance && status !== "upcoming" && (
                    <span className="text-[10px] text-[#94A3B8] italic ml-1">
                      Gestão de presenças indisponível (fora da janela)
                    </span>
                  )}
                </div>

                {/* Add student search panel */}
                {addStudentLessonId === lesson.lesson_id && (
                  <div className="mx-3 sm:mx-4 mb-2 p-3 bg-[#6697a4]/5 rounded-xl border border-[#6697a4]/10" data-testid={`prof-add-student-panel-${lesson.lesson_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-[#475569]">Adicionar aluno à aula</span>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-xs text-[#6697a4]" 
                          onClick={() => setShowAddStudentCreate(!showAddStudentCreate)}
                          data-testid={`prof-toggle-create-student-${lesson.lesson_id}`}
                        >
                          <UserPlus className="h-3 w-3 mr-1" /> Criar Novo
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setAddStudentLessonId(null); setShowAddStudentCreate(false); }}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Inline create new student */}
                    {showAddStudentCreate && (
                      <div className="mb-3 p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                        <Input 
                          placeholder="Nome *" 
                          value={addNewStudent.name} 
                          onChange={(e) => setAddNewStudent({...addNewStudent, name: e.target.value})} 
                          className="h-8 text-sm" 
                          data-testid={`prof-new-student-name-${lesson.lesson_id}`}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input 
                            placeholder="Email (opcional)" 
                            value={addNewStudent.email} 
                            onChange={(e) => setAddNewStudent({...addNewStudent, email: e.target.value})} 
                            className="h-8 text-sm" 
                            data-testid={`prof-new-student-email-${lesson.lesson_id}`}
                          />
                          <Input 
                            placeholder="Telefone (opcional)" 
                            value={addNewStudent.phone} 
                            onChange={(e) => setAddNewStudent({...addNewStudent, phone: e.target.value})} 
                            className="h-8 text-sm" 
                            data-testid={`prof-new-student-phone-${lesson.lesson_id}`}
                          />
                        </div>
                        <Button 
                          size="sm" 
                          onClick={handleCreateAndAddStudent} 
                          disabled={addStudentLoading}
                          className="bg-[#6697a4] hover:bg-[#567f8a] h-7 text-xs" 
                          data-testid={`prof-create-add-student-btn-${lesson.lesson_id}`}
                        >
                          {addStudentLoading ? "A criar..." : "Criar e Adicionar à Aula"}
                        </Button>
                      </div>
                    )}

                    {/* Search existing students */}
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
                      <Input
                        placeholder="Pesquisar aluno..."
                        value={addStudentSearch}
                        onChange={(e) => setAddStudentSearch(e.target.value)}
                        className="pl-8 h-8 text-sm"
                        data-testid={`prof-add-student-search-${lesson.lesson_id}`}
                      />
                    </div>
                    {addStudentResults.length > 0 && (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {addStudentResults.map(s => (
                          <button
                            key={s.user_id}
                            onClick={() => handleAddStudent(s.user_id)}
                            disabled={addStudentLoading}
                            className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white transition-colors text-left"
                            data-testid={`prof-add-student-option-${s.user_id}`}
                          >
                            <Avatar className="h-7 w-7 shrink-0">
                              <AvatarImage src={s.picture} />
                              <AvatarFallback className="bg-[#6697a4] text-white text-[10px]">{s.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-[#0F172A] truncate">{s.name || s.email}</p>
                              <p className="text-[10px] text-[#94A3B8]">{s.credits || 0} créditos · {s.private_credits || 0} privados</p>
                            </div>
                            <UserPlus className="h-4 w-4 text-[#6697a4] shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                    {addStudentSearch.trim() && addStudentResults.length === 0 && (
                      <p className="text-xs text-[#94A3B8] text-center py-2">Nenhum aluno encontrado</p>
                    )}
                  </div>
                )}

                {/* Student list */}
                <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                  {allEnrollments.length === 0 ? (
                    <p className="text-[#94A3B8] text-xs text-center py-4">
                      Nenhum aluno inscrito nesta aula
                    </p>
                  ) : (
                    <div className="space-y-2 mt-1">
                      <p className="text-xs font-medium text-[#475569] mb-2">
                        Alunos ({allEnrollments.length})
                      </p>
                      {allEnrollments.map((enrollment) => (
                        <EnrollmentRow
                          key={enrollment.enrollment_id}
                          enrollment={enrollment}
                          lessonId={lesson.lesson_id}
                          onConfirmAttendance={onConfirmAttendance}
                          onMarkAbsent={onMarkAbsent}
                          onRevertAttendance={onRevertAttendance}
                          canManageAttendance={canManageAttendance}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

const EnrollmentRow = ({ enrollment, lessonId, onConfirmAttendance, onMarkAbsent, onRevertAttendance, canManageAttendance }) => {
  const isConfirmed = enrollment.status === "confirmed";
  const isAbsent = enrollment.status === "absent";
  const isPending = !isConfirmed && !isAbsent && enrollment.status !== "cancelled";

  return (
    <div
      className={`p-2.5 sm:p-3 rounded-xl ${
        isConfirmed
          ? "bg-[#10B981]/5 border border-[#10B981]/20"
          : isAbsent
          ? "bg-red-50 border border-red-100"
          : "bg-slate-50"
      }`}
      data-testid={`prof-enrollment-${enrollment.enrollment_id}`}
    >
      <div className="flex items-center gap-2.5">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={enrollment.user?.picture} />
          <AvatarFallback className="bg-[#6697a4] text-white text-xs">
            {enrollment.user?.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[#0F172A] text-sm truncate">
            {enrollment.user?.name}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] h-4">
              {enrollment.activity_type || "surf"}
            </Badge>
            {enrollment.status === "reserved" && (
              <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] h-4">
                Reserva
              </Badge>
            )}
            {isConfirmed && (
              <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-[10px] h-4">
                Presente
              </Badge>
            )}
            {isAbsent && (
              <Badge className="bg-red-100 text-red-600 border-0 text-[10px] h-4">
                Faltou
              </Badge>
            )}
          </div>
        </div>
        {/* Quick feedback link */}
        {enrollment.user?.user_id && (
          <Link
            to={`/professor/students/${enrollment.user.user_id}`}
            className="shrink-0 p-1.5 rounded-lg hover:bg-[#6697a4]/10 text-[#6697a4] transition-colors"
            title="Ver perfil / Dar feedback"
            data-testid={`prof-feedback-link-${enrollment.enrollment_id}`}
          >
            <MessageSquare className="h-4 w-4" />
          </Link>
        )}
      </div>

      {/* Attendance actions - only when time window is open */}
      {canManageAttendance && isPending && (
        <div className="flex gap-1.5 flex-wrap mt-2 ml-[42px]">
          <Button
            size="sm"
            onClick={() => onConfirmAttendance(enrollment.enrollment_id)}
            className="bg-[#10B981] hover:bg-[#059669] h-7 text-xs px-2.5"
            data-testid={`prof-confirm-${enrollment.enrollment_id}`}
          >
            <Check className="mr-1 h-3 w-3" /> Presente
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs px-2.5"
            onClick={() => onMarkAbsent(enrollment.enrollment_id, false)}
            data-testid={`prof-absent-${enrollment.enrollment_id}`}
          >
            <X className="h-3 w-3 mr-1" /> Faltou
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-amber-600 hover:bg-amber-50 h-7 text-xs px-2.5"
            onClick={() => onMarkAbsent(enrollment.enrollment_id, true)}
            data-testid={`prof-absent-refund-${enrollment.enrollment_id}`}
          >
            + Devolver
          </Button>
        </div>
      )}
      {/* Revert button - only when time window is open */}
      {canManageAttendance && (isConfirmed || isAbsent) && onRevertAttendance && (
        <div className="flex gap-1.5 mt-2 ml-[42px]">
          <Button
            size="sm"
            variant="ghost"
            className="text-[#6697a4] hover:bg-[#6697a4]/10 h-7 text-xs px-2.5"
            onClick={() => onRevertAttendance(enrollment.enrollment_id)}
            data-testid={`prof-revert-${enrollment.enrollment_id}`}
          >
            <Undo2 className="h-3 w-3 mr-1" /> Reverter
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfessorLessonsList;
